// Maps a learner's reading guides (the LIST wire shape) into the Home page's
// "set" model: title, author, reading band, relative-created label, the four
// game names, and whether the parent uploaded it. Band comes from the book's
// per-stage fit; game names from the template registry (mechanic id → name).

import { formatDistanceToNow } from 'date-fns'
import { lookupFit } from '@/lib/lookupFit'
import { displayOrder } from '@/lib/layerLabels'
import type { ReadingGuide } from '@/types'
import type { GameTemplateRecord } from '@/hooks/useGameTemplates'

// The design's band keys (just_right → 'just').
export type HomeBand = 'comfort' | 'just' | 'growth' | 'reach'

export interface HomeSet {
  id: string
  title: string
  author: string | null
  band: HomeBand | null
  created: string
  games: string[]
  fromYou: boolean
  generationStatus: ReadingGuide['generationStatus']
}

const BAND_KEY: Record<string, HomeBand> = {
  comfort: 'comfort',
  just_right: 'just',
  growth: 'growth',
  reach: 'reach',
}

function titleCase(id: string): string {
  return id.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function relativeCreated(createdAt: Date): string {
  // "3 days ago", "about 2 hours ago" → trimmed; sub-minute → "just now".
  const s = formatDistanceToNow(createdAt, { addSuffix: true }).replace(/^about /, '')
  return /less than a minute ago/.test(s) ? 'just now' : s
}

function guideGameSummary(guide: ReadingGuide): { mechanicId: string; layerNumber: number }[] {
  if (guide.gameSummary?.length) return guide.gameSummary

  return (guide.gameRows ?? []).map((g) => ({
    mechanicId: g.mechanicId,
    layerNumber: g.layerNumber,
  }))
}

export function toHomeSet(
  guide: ReadingGuide,
  learnerStage: number | null,
  templatesByMechanic: Record<string, GameTemplateRecord>,
): HomeSet {
  const fit = lookupFit(guide.book?.fitProfile, learnerStage)
  const band = fit ? BAND_KEY[fit.classification] ?? null : null

  const games = guideGameSummary(guide)
    .slice()
    .sort((a, b) => displayOrder(a.layerNumber) - displayOrder(b.layerNumber))
    .map((g) => templatesByMechanic[g.mechanicId]?.name ?? titleCase(g.mechanicId))

  return {
    id: guide.id,
    title: guide.title,
    author: guide.book?.author ?? null,
    band,
    created: relativeCreated(guide.createdAt),
    games,
    fromYou: guide.book?.source === 'USER_UPLOAD',
    generationStatus: guide.generationStatus ?? null,
  }
}

/** Newest-first list of Home sets. */
export function toHomeSets(
  guides: ReadingGuide[],
  learnerStage: number | null,
  templates: GameTemplateRecord[] | undefined,
): HomeSet[] {
  const byMechanic: Record<string, GameTemplateRecord> = {}
  for (const t of templates ?? []) byMechanic[t.mechanic_id] = t
  return guides
    .slice()
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    .map((g) => toHomeSet(g, learnerStage, byMechanic))
}
